import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useImperativeHandle, useRef } from "react";
import { useUniqueId } from "../../common/hooks";
import { requiredIf } from "../../common/proptypes";
import { Button } from "../..";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
/**
 * DisclosureListItem is a React component that is intended to be used as a
 * child of DisclosureList. It represents one menu item button and associated
 * content.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
const DisclosureListItem = /*#__PURE__*/React.forwardRef((_ref, userRef) => {
  let {
    "aria-label": ariaLabel,
    customStrings,
    label,
    onClick,
    onKeyDown,
    footer,
    index,
    activeIndex,
    itemId,
    children // eslint-disable-line no-unused-vars

  } = _ref;
  const theme = useTheme();
  const labelId = useUniqueId("pnr-diclosurelistitem-label-");
  const {
    cssRoot,
    cssFooter
  } = extractTheme({
    theme
  });

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const fullAriaLabel = index === activeIndex ? `${ariaLabel}, ${componentStrings.selected}` : ariaLabel;
  const buttonRef = useRef();
  /**
   * Allows calling of focus method and returning a button reference
   * using a user supplied ref.
   */

  useImperativeHandle(userRef, () => ({
    focus: () => buttonRef.current.focus(),
    getButtonRef: () => buttonRef.current
  }));
  const itemClassNames = clsx("pnr-disclosure-list--item", {
    "pnr-disclosure-list--item-active": index === activeIndex
  });
  const footerClassNames = clsx("pnr-disclosure-list--item", "pnr-disclosure-list--footer");
  /**
   * Return a list button type item or a footer item.
   */

  return !footer ? _jsx(Button, {
    ref: buttonRef,
    className: itemClassNames,
    kind: "simple",
    fullWidth: true,
    "aria-describedby": labelId,
    "aria-label": fullAriaLabel,
    onClick: () => onClick(itemId, index),
    onKeyDown: event => onKeyDown(event, itemId, index, footer),
    css: cssRoot,
    children: _jsx("div", {
      id: labelId,
      children: label
    })
  }) : // eslint-disable-next-line jsx-a11y/no-static-element-interactions
  _jsx("div", {
    ref: buttonRef,
    className: footerClassNames,
    tabIndex: 0,
    onKeyDown: event => onKeyDown(event, itemId, index, footer),
    css: cssFooter,
    children: label
  });
});
DisclosureListItem.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Content of list Button. */
  label: PropTypes.node.isRequired,

  /** Current DisclosureListItem active index */
  activeIndex: PropTypes.number,

  /** Aria label for Button and for  */
  "aria-label": requiredIf(PropTypes.string, (props, propName) => !props[propName] && !props.footer, "when 'footer' is not supplied."),

  /** Content for uncontrolled DisclosureList */
  children: PropTypes.node,

  /** Strings used within the component. */
  customStrings: PropTypes.shape(),

  /** Whether or not this DisclosureListItem is a footer */
  footer: PropTypes.bool,

  /** Index of DisclosureListItem */
  index: PropTypes.number,

  /** Id of DisclosureListItem. */
  itemId: PropTypes.string,

  /** Callback to be called when Button is clicked. */
  onClick: PropTypes.func,

  /** Callback to be called on keydown of the Button. */
  onKeyDown: PropTypes.func
} : {};
DisclosureListItem.defaultProps = {
  ["aria-label"]: null,
  itemId: null,
  footer: false,
  children: null,
  customStrings: null,
  index: null,
  activeIndex: null,
  onClick: null,
  onKeyDown: null
};
DisclosureListItem.displayName = "DisclosureListItem";
export default DisclosureListItem;